import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { waitSeconds } from '@reflact/tsutil';
import { GlobalLoadingService } from '@shared-angular/services/global-loading.service';
import { SocketAdminService } from '@shared-angular/services/socketadmin.service';
import { AdminMessage, HelpRequest, HistoryItemContext, HistoryItemResponse, SocketEvent, SupervisorFunction, ThreadSummaryItem } from '@shared/index';
import dayjs from 'dayjs';
import { firstValueFrom } from 'rxjs';

type UserInfo = { _id: string, name: string | null, metadata: string, admin: boolean, isGuest: boolean, guestId: string | null }

type ThreadContext = {
  threadId: string
}

type ExecResultListem = {
  threadId: string,
  date: Date
  errors: unknown[]
  results: unknown[]
}

type keyValue = {
  key: string
  value: string
  type: string
  _id: string
}
@Injectable({
  providedIn: 'root'
})
export class SupervisorService {
  public lastJsExecutionResults: ExecResultListem[] = [];
  private selectedBot: string = '';
  public historyItems: HistoryItemResponse[] = [];
  public threadSummaries: ThreadSummaryItem[] = [];
  public selectedThread?: HistoryItemResponse[];
  public allMeasuresByMessageId: Map<string, number> = new Map<string, number>();
  public threads: HistoryItemResponse[] = [];
  public filteredThreads: HistoryItemResponse[] = [];
  public totalThreads: number = 0;
  public threadMessageCounts: Record<string, number> = {};
  private threadsPageLoading = false;
  public notifications: string[] = [];
  public currentActiveThreads: string[] = [];
  public currentActiveHelpTriggers: HelpRequest[] = [];
  public adminMessages = new Map<number, AdminMessage[]>(); // Map<indexOfMessageInSelectedThread, AdminsMessage[]>
  public keyVals: keyValue[] = [];
  public socketEvents: SocketEvent[] = [];
  public selectedThreadSocketEvents?: SocketEvent[];
  private notify = new Audio();
  private lastSearchString: string = '';

  constructor(private http: HttpClient, private socketAdminService: SocketAdminService, private router: Router, private loadingService: GlobalLoadingService) {
    this.socketAdminService.onSupervisorHistoryItemUpdate.subscribe(async (data: { historyItem: HistoryItemResponse }) => {
      if (data.historyItem.context.botid == this.selectedBot) {
        this.historyItems.push(data.historyItem);
        if (this.notifications.includes(data.historyItem.context.user)) {
          this.playNotification();
        }
        if (this.selectedThread?.[0] && this.isHistoryItemPartOfTheSameThread(data.historyItem, this.selectedThread[0])) {
          this.selectedThread.push(data.historyItem);
          await this.scrollDown();
        }
        this.addHistoryItemToThreads(data.historyItem);
        this.sortThreads();
        this.calculateMeasurements();
      }
    });

    this.socketAdminService.onThreadSummaryUpdate.subscribe((data) => {
      if (data.summeryItem.botId == this.selectedBot) {
        const existingIndex = this.threadSummaries.findIndex(ts => ts.threadId == data.summeryItem.threadId);
        if (existingIndex == -1) {
          this.threadSummaries.push(data.summeryItem);
        } else {
          this.threadSummaries[existingIndex] = data.summeryItem;
        }
      }
    });


    this.socketAdminService.onSupervisorHistoryItemDelete.subscribe((data: { historyItemId: string }) => {
      const itemInHistoryIndex = this.historyItems.findIndex(historyItem => historyItem._id === data.historyItemId);
      const threadId = this.historyItems[itemInHistoryIndex]?.threadId;
      if (itemInHistoryIndex > -1) {
        this.historyItems.splice(itemInHistoryIndex, 1);
      }
      if (threadId) {
        if (this.selectedThread?.[0]?.threadId == threadId) {
          // ist in meiner aktuellen Selection
          this.selectedThread = this.historyItems.filter(historyItem => {
            return this.isHistoryItemPartOfTheSameThread(historyItem, { threadId });
          });
          this.sortSelectedThread();
          if (this.selectedThread.length == 0) {
            this.threads = [...this.threads.filter(hi => hi._id != data.historyItemId)];
            delete this.selectedThread;
            this.filterThreads(this.lastSearchString);
            void this.router.navigate(['/bot-admin', this.selectedBot, 'supervisor']);
            return;
          }
        }
        const threadIndex = this.threads.findIndex(historyItem => historyItem._id === data.historyItemId);
        if (threadIndex == -1) return;
        const firstInThread = this.historyItems.find(a => a.threadId == threadId);
        if (firstInThread) {
          this.threads[threadIndex] = firstInThread;
        } else {
          this.threads = this.threads.filter(hi => hi.threadId != threadId);
        }
        this.threads = [...this.threads];
      }
      this.calculateMeasurements();

    });

    this.socketAdminService.onSupervisorActiveThreads.subscribe((data: { activeThreads: string[], botId: string }) => {
      if (data.botId == this.selectedBot) {
        this.currentActiveThreads = data.activeThreads;
      }
    });

    this.socketAdminService.onSupervisorHelpRequest.subscribe((data: { helpRequests: HelpRequest[], botId: string }) => {
      if (data.botId == this.selectedBot) {
        this.currentActiveHelpTriggers = data.helpRequests;
      }
    });

    this.socketAdminService.onSupervisorUserAnswered.subscribe((data: { message: AdminMessage }) => {
      this.adminMessages.forEach(async adminMessages => {
        const existingMessage = adminMessages.find(am => am._id == data.message._id);
        if (existingMessage) {

          existingMessage.userAnswer = data.message.userAnswer;
          await this.scrollDown();
        }
      });
    });

    this.socketAdminService.onSocketEvent.subscribe((data: { socketEvent: SocketEvent }) => {
      if (data.socketEvent.context.botid != this.selectedBot) return;
      this.socketEvents.push(data.socketEvent);
      if (this.selectedThreadSocketEvents && this.selectedThread) {
        if (this.selectedThread[0]?.threadId == data.socketEvent.openAiThreadId) {
          this.selectedThreadSocketEvents.push(data.socketEvent);
        }
      }
    });

  }

  public userIdToName: Map<string, string> = new Map<string, string>();
  private pendingUserIds = new Set<string>();

  public async updateSingleUserName(userid: string) {
    const user = await firstValueFrom(this.http.get<UserInfo | null>(`/api/toolbox/supervisor/userinfo/${this.selectedBot}/${userid}`));
    this.userIdToName.set(userid, this.userInfoToName(user));
    this.pendingUserIds.delete(userid);
  }

  public getUserIdToName(userid: string): string {
    const userName = this.userIdToName.get(userid);
    if (userName != null) {
      return userName;
    }
    if (!this.pendingUserIds.has(userid)) {
      this.pendingUserIds.add(userid);
      void this.updateSingleUserName(userid);
    }
    return "";
  }

  private userInfoToName(userInfo: UserInfo | null) {
    if (userInfo == null) {
      return "";
    } else if (userInfo.admin && userInfo.name) {
      return userInfo.name;
    } else if (userInfo.guestId != null && !userInfo.isGuest) {
      return userInfo.guestId;
    } else {
      return "";
    }
  }


  public hasHelpRequest(historyItem: HistoryItemResponse) {
    return this.getHelpRequestsFrom(historyItem).length > 0;
  }

  public getHelpRequestsFrom(historyItem: HistoryItemResponse) {
    return this.currentActiveHelpTriggers.filter((helpRequest: HelpRequest) => helpRequest.historyItemId == historyItem._id);
  }



  // Verbindet den Socket-Room für den Bot, ohne History/User-Daten zu laden (z. B. beim Öffnen des Bot-Editors)
  public joinSupervisorView(botId: string): void {
    if (this.selectedBot && this.selectedBot !== botId) {
      void this.socketAdminService.leaveSupervisorView(this.selectedBot);
      delete this.selectedThread;
    }
    if (this.selectedBot === botId) return; // already joined
    this.selectedBot = botId;
    void this.socketAdminService.joinSupervisorView(botId);
  }

  public async loadThreadsPage(botId: string, first: number, rows: number, minMessages = 0): Promise<void> {
    if (this.threadsPageLoading) return;
    this.threadsPageLoading = true;
    try {
      this.joinSupervisorView(botId);
      this.loadingService.loading = true;
      const { threads, total, messageCounts, userInfos } = await firstValueFrom(
        this.http.get<{
          threads: HistoryItemResponse[];
          total: number;
          messageCounts: Record<string, number>;
          userInfos: UserInfo[];
        }>(`/api/toolbox/supervisor/threads/${botId}`, { params: { first, rows, minMessages } })
      );
      this.loadingService.loading = false;
      this.totalThreads = total;
      this.threadMessageCounts = { ...this.threadMessageCounts, ...messageCounts };
      this.threads = threads;
      this.filteredThreads = threads;
      userInfos.forEach(u => { this.userIdToName.set(u._id, this.userInfoToName(u)); });
    } finally {
      this.threadsPageLoading = false;
      this.loadingService.loading = false;
    }
  }

  public async getHistoryItems(botId: string): Promise<HistoryItemResponse[]> {
    this.joinSupervisorView(botId);
    this.threads = [];
    this.filteredThreads = [];
    this.loadingService.loading = true;
    const { historyItems, userInfos, socketEvents } = await firstValueFrom(this.http.get<{ historyItems: HistoryItemResponse[], userInfos: UserInfo[], socketEvents: SocketEvent[] }>('/api/toolbox/supervisor/historyitems/' + botId));
    this.loadingService.loading = false;
    this.historyItems = historyItems;
    for (const historyItem of this.historyItems) {
      this.addHistoryItemToThreads(historyItem);
    }
    userInfos.forEach(u => {
      this.userIdToName.set(u._id, this.userInfoToName(u));
    });

    this.socketEvents = socketEvents;


    this.sortThreads();
    return this.historyItems;
  }

  public leaveSupervisorView(): void {
    if (this.selectedBot) {
      void this.socketAdminService.leaveSupervisorView(this.selectedBot);
    }
  }

  public calculateMeasurements() {
    const searchArray = this.historyItems.toSorted((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());

    for (let i = 0; i < searchArray.length; i++) {
      const hi = searchArray[i];
      if (hi?.role == "assistant") {
        let found = false;
        for (let s = i - 1; !found && s >= 0; s--) {
          const searchItem = searchArray[s];
          if (searchItem?.role == "user" && hi.threadId == searchItem.threadId) {
            found = true;
            this.setTimeDifferenceMS(searchItem, hi);
          }
        }
      }
    }
  }

  public setTimeDifferenceMS(userMessage: HistoryItemResponse, assistanceMessage: HistoryItemResponse) {
    const userTime = new Date(userMessage.created);
    const assistanceTime = new Date(assistanceMessage.created);
    const time: number = assistanceTime.getTime() - userTime.getTime();
    const uniqueKey = assistanceMessage._id;
    if (time < 1000 * 60 * 5) {
      this.allMeasuresByMessageId.set(uniqueKey, time);
    }
  }

  public getStatsForThread(threadId: string) {
    const items = this.historyItems.filter(hi =>
      hi.threadId == threadId &&
      hi.role == "assistant" &&
      this.allMeasuresByMessageId.has(hi._id)).map(hi => {
        // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
        return this.allMeasuresByMessageId.get(hi._id) as number;
      });
    return this.getLatencyStats(items);
  }
  public getStatsForBot() {
    const items = this.historyItems.filter(hi =>
      hi.role == "assistant" &&
      this.allMeasuresByMessageId.has(hi._id)).map(hi => {
        // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
        return this.allMeasuresByMessageId.get(hi._id) as number;
      });
    return this.getLatencyStats(items);
  }

  public getLatencyStats(stats: number[]) {
    let sum = 0;
    let count = 0;

    let sumLess6 = 0;
    let sumLess12 = 0;
    let sumLess24 = 0;
    let sumLessMore = 0;

    stats.forEach(s => {
      sum += s;
      count++;
      if (s < 6000) {
        sumLess6++;
      } else if (s < 12000) {
        sumLess12++;
      } else if (s < 24000) {
        sumLess24++;
      } else {
        sumLessMore++;
      }

    });
    return {
      arith: count == 0 ? 0 : Math.round(sum / count),
      count,
      sum,
      sumLess6,
      sumLess12,
      sumLess24,
      sumLessMore
    };
  }

  public getTimeDifferenceMS(assistanceMessage: HistoryItemResponse) {
    const uniqueKey = assistanceMessage._id;
    return this.allMeasuresByMessageId.get(uniqueKey);
  }

  public async summarizeThread(threadId: string) {
    await firstValueFrom(this.http.get(`/api/toolbox/supervisor/summarizeThread/${this.selectedBot}/${threadId}`));
  }

  public async loadSummariesForBot(botId: string): Promise<void> {
    const result: ThreadSummaryItem[] = await firstValueFrom(this.http.get<ThreadSummaryItem[]>(`/api/toolbox/supervisor/summaries/${botId}`));
    this.threadSummaries = result;
  }

  public getSummeryForThread(threadId: string) {
    const item = this.threadSummaries.find((s) => s.threadId == threadId);
    return item;
  }

  public threadSummeryIsOutdated(threadId: string) {
    const summary = this.getSummeryForThread(threadId);
    if (!summary) return false;
    const lastHistoryItemofThread = this.historyItems.findLast((hi) => {
      return hi.threadId == summary.threadId;
    }
    );
    if (lastHistoryItemofThread == null) {
      console.error("lastHistoryItemofThread is null");
      return true;
    }
    return new Date(summary.created).getTime() < new Date(lastHistoryItemofThread.created).getTime();
  }

  private addHistoryItemToThreads(historyItem: HistoryItemResponse) {
    const index = this.threads.findIndex(thread => this.isHistoryItemPartOfTheSameThread(thread, historyItem));
    if (index > -1 && this.threads[index]) {
      if (historyItem.created < this.threads[index].created) {
        this.threads[index] = historyItem;
      }
    } else {
      this.threads = [...this.threads, historyItem];
    }
  }

  private sortThreads() {
    this.threads.sort((a, b) => {
      if (a.created > b.created) {
        return -1;
      }
      if (a.created < b.created) {
        return 1;
      }
      return 0;
    });
    this.filterThreads(this.lastSearchString);
  }

  private sortSelectedThread() {
    this.selectedThread?.sort((a, b) => {
      if (a.created < b.created) {
        return -1;
      }
      if (a.created > b.created) {
        return 1;
      }
      return 0;
    });
  }

  public async selectThread(threadId: string | null) {
    if (threadId == null) {
      this.selectedThread = undefined;
      this.selectedThreadSocketEvents = undefined;
      return;
    }
    // Use cached messages if already loaded (real-time updates keep this fresh)
    const cached = this.historyItems.filter(hi => this.isHistoryItemPartOfTheSameThread(hi, { threadId }));
    if (cached.length > 0) {
      this.selectedThread = cached;
      this.selectedThreadSocketEvents = this.socketEvents.filter(e =>
        this.isHistoryItemPartOfTheSameThread({ threadId: e.openAiThreadId }, { threadId })
      );
      this.sortSelectedThread();
      await this.scrollDown();
      return;
    }
    // Load messages for this thread on demand
    const { historyItems, socketEvents } = await firstValueFrom(
      this.http.get<{ historyItems: HistoryItemResponse[]; socketEvents: SocketEvent[] }>(
        `/api/toolbox/supervisor/thread/${this.selectedBot}/${threadId}`
      )
    );
    for (const item of historyItems) {
      if (!this.historyItems.find(hi => hi._id === item._id)) this.historyItems.push(item);
    }
    for (const event of socketEvents) {
      if (!this.socketEvents.find(e => e._id === event._id)) this.socketEvents.push(event);
    }
    this.selectedThread = historyItems;
    this.selectedThreadSocketEvents = socketEvents;
    this.calculateMeasurements();
    await this.scrollDown();
  }

  public isHistoryItemPartOfTheSameThread(historyItemA: ThreadContext, historyItemB: ThreadContext) {
    return historyItemA.threadId == historyItemB.threadId;
  }

  public async scrollDown() {
    await waitSeconds(.1);
    const chatRow = document.querySelector('.chat-row:last-child');
    if (chatRow) {
      chatRow.scrollIntoView({ behavior: 'smooth' });
    }
  }

  public toggleNotification(userid: string) {
    if (this.notifications.includes(userid)) {
      this.notifications = this.notifications.filter((n) => n != userid);
    } else {
      this.notifications.push(userid);
    }
  }

  private playNotification() {
    this.notify.src = 'assets/notification.mp3';
    this.notify.load();
    this.notify.play().catch((error: unknown) => {
      console.error('Audio playback error:', error);
    });
  }

  public isThreadActive(threadId: string): boolean {
    return this.currentActiveThreads.includes(threadId);
  }

  public activeThreadsCount() {
    return this.currentActiveThreads.length;
  }

  public async sendAdminMessage(message: string): Promise<AdminMessage | false> {
    if (!this.selectedThread) {
      return false;
    }
    const adminMessage = await firstValueFrom(this.http.post<AdminMessage>("/api/toolbox/bot/admin/adminMessage/" + this.selectedBot, { message, userId: this.selectedThread[0]?.context.user, threadId: this.selectedThread[0]?.threadId }));
    const mapIndex = this.selectedThread.length - 1;
    this.addAdminMessage(mapIndex, adminMessage);

    return adminMessage;
  }

  public async sendAdminMessageToThread(message: string, threadId: string): Promise<AdminMessage | false> {
    const context = this.getContextFromThreadId(threadId);
    if (!context) return false;
    const adminMessage = await firstValueFrom(this.http.post<AdminMessage>("/api/toolbox/bot/admin/adminMessage/" + this.selectedBot, { message, userId: context.user, threadId }));
    if (this.selectedThread?.[0]?.threadId == threadId) {
      const mapIndex = this.selectedThread.length - 1;
      this.addAdminMessage(mapIndex, adminMessage);
    }
    return adminMessage;
  }

  public getContextFromThreadId(threadId: string): HistoryItemContext | null {
    const historyItem = this.threads.find(t => t.threadId == threadId);
    if (historyItem) {
      return historyItem.context;
    }
    return null;
  }

  private addAdminMessage(index: number, adminMessage: AdminMessage) {
    if (!this.adminMessages.has(index)) {
      this.adminMessages.set(index, []);
    }
    this.adminMessages.get(index)?.push(adminMessage);
  }

  public getSocketEvents(index: number): SocketEvent[] {
    if (!this.selectedThreadSocketEvents || !this.selectedThread) return [];
    const current = this.selectedThread[index];
    if (!current) return [];
    const next = this.selectedThread[index + 1];

    return this.selectedThreadSocketEvents.filter(event => {
      const eventTime = new Date(event.created).getTime();
      const currentTime = new Date(current.created).getTime();
      const nextTime = next ? new Date(next.created).getTime() : Infinity;
      return eventTime >= currentTime && eventTime < nextTime;
    }).sort((a, b) => {
      return new Date(a.created).getTime() - new Date(b.created).getTime();
    });
  }

  public getPreFirstMessageSocketEvents(): SocketEvent[] {
    if (!this.selectedThreadSocketEvents || !this.selectedThread) return [];
    const first = this.selectedThread[0];
    if (!first) return [];
    const firstTime = new Date(first.created).getTime();
    return this.selectedThreadSocketEvents
      .filter(event => new Date(event.created).getTime() < firstTime)
      .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
  }

  public getAdminMessages(index: number): AdminMessage[] {
    return this.adminMessages.get(index) ?? [];
  }

  public async loadAdminMessages(): Promise<void> {
    this.adminMessages = new Map();
    if (this.selectedThread) {
      const adminMessages = await firstValueFrom(this.http.get<AdminMessage[]>("/api/toolbox/bot/admin/getAdminMessages/" + this.selectedBot + "/" + this.selectedThread[0]?.threadId));
      this.selectedThread.forEach((historyItem, historyItemIndex) => {
        for (const adminMessage of adminMessages) {
          let nextEntry: HistoryItemResponse | undefined;
          if (typeof (this.selectedThread?.[historyItemIndex + 1]) !== 'undefined') {
            nextEntry = this.selectedThread[historyItemIndex + 1];
          }

          if (
            historyItem.created <= adminMessage.created &&
            (
              typeof nextEntry == 'undefined' ||
              nextEntry.created > adminMessage.created
            )
          ) {
            this.addAdminMessage(historyItemIndex, adminMessage);
          }
        }
      });
    }
  }

  public loadHelpRequests(): void {
    if (this.selectedBot) {
      // Das hier triggert ein socket ioupdate die ergebisse kommen also hintenrum durch die Blume ins frontend
      void firstValueFrom(this.http.get("/api/toolbox/bot/admin/broadcasthelprequests/" + this.selectedBot));
    }
  }

  public notificationUnsolvedSummary(): string {
    const unsolvedCount = this.notificationUnsolvedAll();
    if (unsolvedCount > 99) {
      return "99+";
    } else {
      return unsolvedCount + "";
    }
  }

  public notificationUnsolved(threadId: string): number {
    return this.currentActiveHelpTriggers.filter(helprequest => !helprequest.solved && threadId == helprequest.threadId).length;
  }
  public notificationUnsolvedAll(): number {
    return this.currentActiveHelpTriggers.filter(helprequest => !helprequest.solved).length;
  }

  public notificationCountSolved(threadId: string): number {
    return this.currentActiveHelpTriggers.filter(helprequest => helprequest.solved && threadId == helprequest.threadId).length;
  }

  public notificationCountAll(threadId: string): number {
    return this.currentActiveHelpTriggers.filter(helprequest => threadId == helprequest.threadId).length;
  }

  public solveUserNeedsHelp(helpRequestId: string, solved: boolean) {
    void firstValueFrom(this.http.post("/api/toolbox/bot/admin/updatehelprequest/" + this.selectedBot + "/" + helpRequestId, { solved }));
  }

  public async triggerSupervisorfunction(f: SupervisorFunction) {
    if (!this.selectedThread) {
      return false;
    }
    if (!this.selectedThread[0]?.threadId) {
      return false;
    }
    const threadId = this.selectedThread[0].threadId;
    const io = await this.socketAdminService.getIo();

    return new Promise((resolve, _reject) => {
      io.emit("supervisorExec", { js: f.js, threadId }, (result) => {
        this.lastJsExecutionResults.unshift({

          errors: result.errors,

          results: result.results,
          date: new Date(),
          threadId: threadId
        });
        resolve(result);
      });
    });
  }

  public async triggerSupervisorfunctionForAllActiveClients(f: SupervisorFunction | undefined, botId: string) {
    if (!f) {
      return false;
    }

    const io = await this.socketAdminService.getIo();

    return new Promise((resolve, _reject) => {
      io.emit("supervisorExec", { js: f.js, botId }, (result) => {
        this.lastJsExecutionResults.unshift({
          errors: result.errors,
          results: result.results,
          date: new Date(),
          threadId: 'allActiveClients'
        });
        resolve(result);
      });
    });
  }

  public async triggerSupervisorfunctionToThread(f: SupervisorFunction, threadId: string) {
    const io = await this.socketAdminService.getIo();
    return new Promise((resolve, _reject) => {
      io.emit("supervisorExec", { js: f.js, threadId }, (result) => {
        this.lastJsExecutionResults.unshift({

          errors: result.errors,

          results: result.results,
          date: new Date(),
          threadId: threadId
        });
        resolve(result);
      });
    });
  }

  public getSelectedBot(): string {
    return this.selectedBot;
  }

  public getMessageCountPerThread(threadId: string) {
    return this.threadMessageCounts[threadId] ?? this.historyItems.filter(t => t.threadId == threadId).length;
  }

  public async loadKeyVal(): Promise<void> {
    if (this.selectedThread) {
      const keyVals = await firstValueFrom(this.http.get<{ "keyVal": keyValue[] }>("/api/toolbox/bot/admin/getKeyVal/" + this.selectedBot + "/" + this.selectedThread[0]?.context.user));
      this.keyVals = keyVals.keyVal;
    }
  }

  public filterThreads(searchString: string) {
    if (searchString !== '') {
      if (searchString !== this.lastSearchString) {
        this.lastSearchString = searchString;
        this.filteredThreads = this.threads.filter((t) => {
          return dayjs(t.created).format('DD.MM.YYYY').includes(searchString)
            || this.getUserIdToName(t.context.user).includes(searchString)
            || this.historyItems.filter(hi => hi.threadId === t.threadId).find(item => {
              const filtered = item.content.filter(c => c.type === 'text' && c.text.includes(searchString));
              if (filtered.length > 0) return true;
              return false;
            });
        });
      }
    } else {
      this.filteredThreads = this.threads;
    }
  }
}